import { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { userSettingsApi, voiceProfileApi } from "../lib/api";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePipedreamConnect } from "../lib/usePipedreamConnect";
import {
  Settings, Save, Loader2, Linkedin, Facebook, Instagram,
  Palette, Upload, Image as ImageIcon, Check,
  ChevronDown, Key, AlertTriangle, PenLine, Link2, Twitter,
  Mic, Target, FileText, Sparkles
} from "lucide-react";
import { fbLogin } from "../lib/facebook";

// ─── Voice Profile Constants (Bloque 1) ───

const FORMALIDAD_OPTIONS = [
  { id: "formal", label: "Formal" },
  { id: "semiformal", label: "Semiformal" },
  { id: "informal", label: "Informal" },
];

const CERCANIA_OPTIONS = [
  { id: "distante", label: "Distante" },
  { id: "neutro", label: "Neutro" },
  { id: "cercano", label: "Cercano" },
  { id: "intimo", label: "Intimo" },
];

const ENERGIA_OPTIONS = [
  { id: "reflexivo", label: "Reflexivo" },
  { id: "equilibrado", label: "Equilibrado" },
  { id: "dinamico", label: "Dinamico" },
  { id: "intenso", label: "Intenso" },
];

const POSTURA_OPTIONS = [
  { id: "experto que enseña", label: "Experto que enseña" },
  { id: "igual que conversa", label: "Igual que conversa" },
  { id: "guía que acompaña", label: "Guia que acompaña" },
  { id: "provocador que cuestiona", label: "Provocador que cuestiona" },
];

const TONOS_HABITUALES = [
  "Profesional", "Tecnico", "Cercano y humano", "Motivador", "Empatico",
  "Didactico", "Informativo", "Conversacional", "Inspirador", "Directo", "Humoristico",
];

const TONOS_CARACTER = [
  "Ironico", "Sarcastico", "Caricaturesco", "Dramatico", "Periodistico",
  "Filosofico", "Socratico", "Poetico", "Mistico", "Epico", "Minimalista",
];

// ─── Bloque 2 Constants ───

const ANGULO_OPTIONS = [
  { id: "Problema → Solución", label: "Problema → Solucion" },
  { id: "Caso real", label: "Caso real" },
  { id: "Datos y tendencia", label: "Datos y tendencia" },
  { id: "Contraintuitivo", label: "Contraintuitivo" },
  { id: "Paso a paso", label: "Paso a paso" },
  { id: "Comparativa", label: "Comparativa" },
  { id: "Opinión experta", label: "Opinion experta" },
  { id: "Futuro / Prospectiva", label: "Futuro / Prospectiva" },
];

const OBJETIVO_OPTIONS = [
  { id: "Informar", label: "Informar" },
  { id: "Didáctico", label: "Didactico" },
  { id: "Práctico / Aplicable", label: "Practico / Aplicable" },
  { id: "Generar autoridad", label: "Generar autoridad" },
  { id: "Controversial", label: "Controversial" },
  { id: "Captar leads", label: "Captar leads" },
  { id: "Fidelizar comunidad", label: "Fidelizar comunidad" },
  { id: "Viralizable", label: "Viralizable" },
];

// ─── Existing constants ───

const ENGINES = [
  { id: "openai", label: "GPT Image 1", desc: "Fotorrealista y detallado" },
  { id: "nano_banana", label: "Nano Banana", desc: "Creativo y artistico" },
];

const STYLES = [
  { id: "fotorrealista", label: "Fotorrealista" },
  { id: "3d", label: "3D" },
  { id: "ilustracion", label: "Ilustracion" },
  { id: "minimalista", label: "Minimalista" },
  { id: "corporativo", label: "Corporativo" },
  { id: "artistico", label: "Artistico" },
  { id: "geometrico", label: "Geometrico" },
  { id: "infografico", label: "Infografico" },
  { id: "editorial", label: "Editorial" },
  { id: "cinematografico", label: "Cinematografico" },
  { id: "comic", label: "Comic" },
  { id: "pixar", label: "Pixar / 3D expresivo" },
  { id: "manga", label: "Manga" },
  { id: "acuarela", label: "Acuarela" },
  { id: "vintage", label: "Vintage / Retro" },
  { id: "street_art", label: "Street Art" },
  { id: "scifi", label: "Sci-Fi / Futurista" },
  { id: "flat_design", label: "Flat Design" },
  { id: "organico", label: "Organico / Natural" },
  { id: "dark_dramatico", label: "Dark / Dramatico" },
];

const MOODS = [
  { id: "luminoso", label: "Luminoso" },
  { id: "oscuro", label: "Oscuro" },
  { id: "neutro", label: "Neutro" },
  { id: "calido", label: "Calido" },
  { id: "frio", label: "Frio" },
];

const NETWORKS = [
  { id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2",
    hasOAuth: false,
    credFields: [
      { key: "access_token", label: "Access Token", placeholder: "Token de acceso de LinkedIn" },
      { key: "organization_id", label: "Organization ID (opcional)", placeholder: "ID de la empresa" },
    ]},
  { id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2",
    hasOAuth: true,
    credFields: [
      { key: "page_id", label: "Page ID", placeholder: "Se rellena automaticamente al conectar" },
      { key: "page_name", label: "Pagina", placeholder: "Nombre de la pagina", readOnly: true },
      { key: "access_token", label: "Page Access Token", placeholder: "Se rellena automaticamente al conectar" },
    ]},
  { id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C",
    hasOAuth: true,
    credFields: [
      { key: "ig_user_id", label: "Instagram Business ID", placeholder: "Se rellena al conectar Facebook" },
      { key: "username", label: "Usuario", placeholder: "@tuempresa" },
      { key: "access_token", label: "Access Token", placeholder: "Se rellena al conectar Facebook" },
    ]},
  { id: "x", label: "X (Twitter)", icon: Twitter, color: "#000000",
    hasOAuth: false,
    credFields: [
      { key: "api_key", label: "API Key", placeholder: "Tu API Key de X" },
      { key: "api_secret", label: "API Secret", placeholder: "Tu API Secret" },
      { key: "access_token", label: "Access Token", placeholder: "Token de acceso" },
      { key: "access_secret", label: "Access Token Secret", placeholder: "Secret del token" },
    ]},
];

// ─── Network Card Component ───

function NetworkCard({ net, data, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const enabled = data?.enabled ?? true;
  const credentials = data?.credentials || {};

  const update = (field, value) => onChange({ ...data, [field]: value });
  const updateCred = (key, value) => onChange({ ...data, credentials: { ...credentials, [key]: value } });

  const handleFacebookConnect = async () => {
    setConnecting(true);
    try {
      const result = await fbLogin();
      if (result.pages && result.pages.length > 0) {
        const page = result.pages[0];
        onChange({ ...data, enabled: true, credentials: { page_id: page.id, page_name: page.name, access_token: page.access_token, user_token: result.accessToken } });
        if (page.instagram_business_account) {
          toast.success(`Facebook conectado: ${page.name}. Instagram Business detectado.`);
        } else {
          toast.success(`Facebook conectado: ${page.name}`);
        }
      } else {
        toast.error("No se encontraron paginas de Facebook.");
      }
    } catch (err) {
      toast.error(err.message || "Error al conectar con Facebook");
    } finally { setConnecting(false); }
  };

  const isConnected = credentials.access_token && credentials.access_token.length > 10;

  return (
    <div data-testid={`network-card-${net.id}`} className="rounded-xl border transition-all duration-200 overflow-hidden"
      style={{ background: "#0F2035", borderColor: enabled ? `${net.color}30` : "#1E3A5F" }}>
      <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ background: enabled ? `${net.color}15` : "rgba(148,163,184,0.05)" }}>
            <net.icon className="w-5 h-5" style={{ color: enabled ? net.color : "#475569" }} />
          </div>
          <div>
            <div className="text-sm font-medium" style={{ color: enabled ? "#E2E8F0" : "#64748B" }}>{net.label}</div>
            <div className="text-xs" style={{ color: "#94A3B8" }}>
              {enabled ? (isConnected ? "Conectado" : "Activo") : "Desactivado"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div data-testid={`network-toggle-${net.id}`} onClick={(e) => { e.stopPropagation(); update("enabled", !enabled); }}
            className="w-10 h-6 rounded-full p-0.5 transition-all duration-200 cursor-pointer"
            style={{ background: enabled ? "#00D4FF" : "#1E3A5F" }}>
            <div className="w-5 h-5 rounded-full transition-all duration-200"
              style={{ background: "#0B1622", transform: enabled ? "translateX(16px)" : "translateX(0)" }} />
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} style={{ color: "#94A3B8" }} />
        </div>
      </div>
      {expanded && enabled && (
        <div className="px-4 pb-4 space-y-4 animate-fade-in-up" style={{ borderTop: "1px solid #1E3A5F" }}>
          <div className="pt-4">
            <Label className="text-xs flex items-center gap-1.5 mb-2" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>
              <Key className="w-3 h-3" />Credenciales de {net.label}
            </Label>
            {net.hasOAuth && (net.id === "facebook" || net.id === "instagram") && (
              <div className="mb-3">
                {isConnected ? (
                  <div className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }}>
                    <Check className="w-4 h-4" style={{ color: "#4ADE80" }} />
                    <span className="text-xs" style={{ color: "#4ADE80" }}>Conectado{credentials.page_name ? `: ${credentials.page_name}` : ""}</span>
                    <button onClick={handleFacebookConnect} className="ml-auto text-xs px-2 py-1 rounded" style={{ color: "#94A3B8" }}>Reconectar</button>
                  </div>
                ) : (
                  <Button data-testid={`connect-${net.id}-btn`} onClick={handleFacebookConnect} disabled={connecting}
                    variant="outline" size="sm" className="w-full rounded-lg text-xs h-9"
                    style={{ borderColor: `${net.color}40`, color: net.color, background: `${net.color}08` }}>
                    {connecting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1.5" />}
                    {connecting ? "Conectando..." : `Conectar con ${net.id === "instagram" ? "Facebook/Meta" : net.label}`}
                  </Button>
                )}
              </div>
            )}
            <div className="space-y-2">
              {net.credFields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs mb-0.5 block" style={{ color: "#64748B" }}>{f.label}</Label>
                  <Input data-testid={`${net.id}-cred-${f.key}`}
                    type={f.key.includes("token") || f.key.includes("secret") ? "password" : "text"}
                    placeholder={f.placeholder} value={credentials[f.key] || ""}
                    readOnly={f.readOnly}
                    onChange={(e) => updateCred(f.key, e.target.value)}
                    className="h-9 text-xs" style={{ background: f.readOnly ? "#0a1018" : "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dimension Selector Component ───

function DimensionSelector({ label, description, options, value, onChange, testPrefix }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold block" style={{ color: "#E2E8F0" }}>{label}</Label>
      {description && <p className="text-sm" style={{ color: "#64748B" }}>{description}</p>}
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button key={opt.id} data-testid={`${testPrefix}-${opt.id}`}
            onClick={() => onChange(opt.id)}
            className="px-4 py-2.5 rounded-full text-sm border transition-all duration-200"
            style={{
              background: value === opt.id ? "rgba(0,212,255,0.1)" : "transparent",
              borderColor: value === opt.id ? "rgba(0,212,255,0.4)" : "#1E3A5F",
              color: value === opt.id ? "#00D4FF" : "#94A3B8",
            }}>
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main Component ───

export default function UserSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("voice");
  const fileInputRef = useRef(null);
  const voiceFileRef = useRef(null);

  // Existing settings state
  const [socialNetworks, setSocialNetworks] = useState({
    linkedin: { enabled: true, credentials: {} },
    facebook: { enabled: true, credentials: {} },
    instagram: { enabled: true, credentials: {} },
    x: { enabled: true, credentials: {} },
  });
  const [visualPrefs, setVisualPrefs] = useState({ default_engine: "openai", default_style: "3d" });
  const [brand, setBrand] = useState({ company_name: "", primary_color: "#1a365d", secondary_color: "#f97316", logo_base64: null });

  // WordPress per-user config
  const [wpConfig, setWpConfig] = useState({ url: "", username: "", app_password: "", has_password: false, configured: false });
  const [wpTestState, setWpTestState] = useState({ loading: false, ok: null, msg: "" });
  const [wpDirty, setWpDirty] = useState(false);

  // Voice Profile state (Bloque 1)
  const [voiceProfile, setVoiceProfile] = useState({
    formalidad: null,
    cercania: null,
    energia: null,
    postura: null,
    tonos_expresivos: [],
    referencia_estilo: "",
    resumen_voz: "",
    nombre_cliente: "",
    sector: "",
    audiencia: "",
    closing_phrase: "",
    affiliate_url: "",
    affiliate_label: "",
    affiliate_description: "",
  });

  // Bloque 2 state
  const [anguloEditorial, setAnguloEditorial] = useState(null);
  const [objetivoContenido, setObjetivoContenido] = useState(null);

  // Voice analysis state
  const [analyzingVoice, setAnalyzingVoice] = useState(false);
  const [voiceAnalysis, setVoiceAnalysis] = useState(null);
  const [showAdvancedTones, setShowAdvancedTones] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, voiceRes, wpRes] = await Promise.all([
        userSettingsApi.get(),
        voiceProfileApi.get(),
        api.get("/user/wordpress").catch(() => null),
      ]);
      const d = settingsRes.data;
      if (d.social_networks) setSocialNetworks(d.social_networks);
      if (d.visual_preferences) setVisualPrefs(d.visual_preferences);
      if (d.brand) setBrand(d.brand);
      if (wpRes?.data) {
        setWpConfig({
          url: wpRes.data.url || "",
          username: wpRes.data.username || "",
          app_password: "",
          has_password: !!wpRes.data.has_password,
          configured: !!wpRes.data.configured,
        });
      }

      const vp = voiceRes.data;
      if (vp) {
        setVoiceProfile({
          formalidad: vp.formalidad || null,
          cercania: vp.cercania || null,
          energia: vp.energia || null,
          postura: vp.postura || null,
          tonos_expresivos: vp.tonos_expresivos || [],
          referencia_estilo: vp.referencia_estilo || "",
          resumen_voz: vp.resumen_voz || "",
          nombre_cliente: vp.nombre_cliente || "",
          sector: vp.sector || "",
          audiencia: vp.audiencia || "",
          closing_phrase: vp.closing_phrase || "",
          affiliate_url: vp.affiliate_url || "",
          affiliate_label: vp.affiliate_label || "",
          affiliate_description: vp.affiliate_description || "",
        });
        if (vp.angulo_editorial) setAnguloEditorial(vp.angulo_editorial);
        if (vp.objetivo_contenido) setObjetivoContenido(vp.objetivo_contenido);
      }
    } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        userSettingsApi.update({ social_networks: socialNetworks, visual_preferences: visualPrefs, brand }),
        voiceProfileApi.update({
          ...voiceProfile,
          angulo_editorial: anguloEditorial,
          objetivo_contenido: objetivoContenido,
        }),
      ]);
      toast.success("Ajustes guardados correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleSaveWordPress = async () => {
    if (!wpConfig.url.trim() || !wpConfig.username.trim()) {
      toast.error("Completa URL y Usuario");
      return;
    }
    if (!wpConfig.app_password.trim() && !wpConfig.has_password) {
      toast.error("Introduce la Application Password de WordPress");
      return;
    }
    setSaving(true);
    try {
      // If user didn't change password but already had one, send empty string -> backend rejects.
      // So we keep stored password by NOT sending PUT in that case (only send when dirty/new).
      const payload = {
        url: wpConfig.url.trim(),
        username: wpConfig.username.trim(),
        app_password: wpConfig.app_password.trim() || (wpConfig.has_password ? "__keep__" : ""),
      };
      // Backend treats "__keep__" as "do not change" by re-using existing password
      // If no existing password, app_password must be real.
      await api.put("/user/wordpress", payload);
      toast.success("WordPress guardado");
      setWpDirty(false);
      // Reload to refresh has_password
      const r = await api.get("/user/wordpress");
      setWpConfig({
        url: r.data.url || "",
        username: r.data.username || "",
        app_password: "",
        has_password: !!r.data.has_password,
        configured: !!r.data.configured,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleTestWordPress = async () => {
    setWpTestState({ loading: true, ok: null, msg: "" });
    try {
      // Auto-save if there are unsaved changes (typical case: user just pasted a
      // fresh Application Password and clicks "Probar" without saving first).
      // Without this, the backend would test with the previously stored password
      // and return 401 even though the new one is correct.
      if (wpDirty) {
        if (!wpConfig.url.trim() || !wpConfig.username.trim()) {
          setWpTestState({ loading: false, ok: false, msg: "Completa URL y Usuario antes de probar" });
          return;
        }
        if (!wpConfig.app_password.trim() && !wpConfig.has_password) {
          setWpTestState({ loading: false, ok: false, msg: "Introduce la Application Password antes de probar" });
          return;
        }
        const payload = {
          url: wpConfig.url.trim(),
          username: wpConfig.username.trim(),
          app_password: wpConfig.app_password.trim() || (wpConfig.has_password ? "__keep__" : ""),
        };
        await api.put("/user/wordpress", payload);
        setWpDirty(false);
        // refresh UI state so has_password becomes true and the password input clears
        const rr = await api.get("/user/wordpress");
        setWpConfig({
          url: rr.data.url || "",
          username: rr.data.username || "",
          app_password: "",
          has_password: !!rr.data.has_password,
          configured: !!rr.data.configured,
        });
      }
      const r = await api.post("/user/wordpress/test");
      if (r.data?.ok) {
        setWpTestState({ loading: false, ok: true, msg: `Conectado como ${r.data.wp_user || "—"}` });
        toast.success("Conexión OK");
      } else {
        setWpTestState({ loading: false, ok: false, msg: r.data?.detail || `HTTP ${r.data?.status || ""}` });
      }
    } catch (err) {
      setWpTestState({ loading: false, ok: false, msg: err.response?.data?.detail || err.message });
    }
  };

  const handleDeleteWordPress = async () => {
    if (!window.confirm("¿Eliminar la configuración de WordPress?")) return;
    try {
      await api.delete("/user/wordpress");
      setWpConfig({ url: "", username: "", app_password: "", has_password: false, configured: false });
      setWpTestState({ loading: false, ok: null, msg: "" });
      toast.success("Configuración eliminada");
    } catch { toast.error("Error al eliminar"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max 2MB"); return; }
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await userSettingsApi.uploadLogo(formData);
      setBrand((prev) => ({ ...prev, logo_base64: res.data.logo }));
      toast.success("Logo subido correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir logo");
    }
  };

  const handleVoiceUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validExts = [".pdf", ".docx", ".txt"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!validExts.includes(ext)) {
      toast.error("Formato no soportado. Usa PDF, DOCX o TXT.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error("Maximo 5MB"); return; }

    setAnalyzingVoice(true);
    setVoiceAnalysis(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await voiceProfileApi.analyzeVoice(formData);
      const jobId = res.data.job_id;
      // Poll for completion
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const status = await voiceProfileApi.analysisStatus(jobId);
        if (status.data.status === "completed") {
          setVoiceAnalysis(status.data.analysis);
          const a = status.data.analysis;
          // Auto-suggest values from analysis
          if (a.resumen_voz) {
            setVoiceProfile((p) => ({ ...p, resumen_voz: a.resumen_voz }));
          }
          toast.success("Analisis de voz completado. Revisa las sugerencias.");
          return;
        }
        if (status.data.status === "invalid") {
          toast.error(status.data.razon || "El texto no es contenido comunicacional valido.");
          return;
        }
        if (status.data.status === "error") {
          toast.error(status.data.detail || "Error al analizar");
          return;
        }
      }
      toast.error("Timeout: el analisis tardo demasiado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar muestra de voz");
    } finally { setAnalyzingVoice(false); }
  };

  const toggleTono = (tono) => {
    setVoiceProfile((prev) => {
      const current = prev.tonos_expresivos || [];
      if (current.includes(tono)) {
        return { ...prev, tonos_expresivos: current.filter((t) => t !== tono) };
      }
      if (current.length >= 3) {
        toast.error("Maximo 3 tonos expresivos");
        return prev;
      }
      return { ...prev, tonos_expresivos: [...current, tono] };
    });
  };

  const applyAnalysis = () => {
    if (!voiceAnalysis) return;
    setVoiceProfile((p) => ({
      ...p,
      formalidad: voiceAnalysis.formalidad || p.formalidad,
      cercania: voiceAnalysis.cercania || p.cercania,
      energia: voiceAnalysis.energia || p.energia,
      postura: voiceAnalysis.postura || p.postura,
      resumen_voz: voiceAnalysis.resumen_voz || p.resumen_voz,
    }));
    toast.success("Valores del analisis aplicados");
  };

  const updateNetwork = (id, data) => setSocialNetworks((prev) => ({ ...prev, [id]: data }));

  const sections = [
    { id: "voice", label: "Perfil de Voz", icon: Mic },
    { id: "angle", label: "Angulo Editorial", icon: Target },
    { id: "networks", label: "Redes Sociales", icon: Linkedin },
    { id: "visual", label: "Estilo Visual", icon: Palette },
    { id: "brand", label: "Marca", icon: ImageIcon },
    { id: "wordpress", label: "WordPress", icon: FileText },
  ];

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "#0B1622" }}>
        <Navbar />
        <div className="flex items-center justify-center py-24" style={{ color: "#94A3B8" }}>
          <Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando ajustes...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="user-settings-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,212,255,0.1)" }}>
              <Settings className="w-5 h-5" style={{ color: "#00D4FF" }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>Ajustes</h1>
              <p className="text-sm" style={{ color: "#94A3B8" }}>Configura tu perfil de voz, estilo y redes sociales</p>
            </div>
          </div>
          <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving}
            className="rounded-full px-6 text-sm font-medium"
            style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-52 flex-shrink-0 hidden md:block">
            <div className="sticky top-24 space-y-1">
              {sections.map((s) => (
                <button key={s.id} data-testid={`settings-tab-${s.id}`} onClick={() => setActiveSection(s.id)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 text-left"
                  style={{
                    background: activeSection === s.id ? "rgba(0,212,255,0.08)" : "transparent",
                    color: activeSection === s.id ? "#00D4FF" : "#94A3B8",
                  }}>
                  <s.icon className="w-4 h-4" />{s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-6">
            {/* Mobile tabs */}
            <div className="flex gap-1 p-1 rounded-lg md:hidden overflow-x-auto" style={{ background: "#0F2035" }}>
              {sections.map((s) => (
                <button key={s.id} onClick={() => setActiveSection(s.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs whitespace-nowrap"
                  style={{ background: activeSection === s.id ? "rgba(0,212,255,0.1)" : "transparent", color: activeSection === s.id ? "#00D4FF" : "#94A3B8" }}>
                  <s.icon className="w-3.5 h-3.5" />{s.label}
                </button>
              ))}
            </div>

            {/* ═══ PERFIL DE VOZ (Bloque 1) ═══ */}
            {activeSection === "voice" && (
              <div className="space-y-4 animate-fade-in-up">
                {/* Client Profile */}
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Sparkles className="w-5 h-5" style={{ color: "#F97316" }} />
                    Datos del Cliente
                  </h2>
                  <p className="text-sm mb-5" style={{ color: "#94A3B8" }}>
                    Esta informacion se inyecta en el prompt maestro para personalizar cada articulo.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm block mb-1" style={{ color: "#94A3B8" }}>Nombre / Marca</Label>
                      <Input data-testid="vp-nombre" value={voiceProfile.nombre_cliente}
                        onChange={(e) => setVoiceProfile({ ...voiceProfile, nombre_cliente: e.target.value })}
                        placeholder="Ej: Alianza Empresarial"
                        className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                    </div>
                    <div>
                      <Label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>Sector</Label>
                      <Input data-testid="vp-sector" value={voiceProfile.sector}
                        onChange={(e) => setVoiceProfile({ ...voiceProfile, sector: e.target.value })}
                        placeholder="Ej: Emprendimiento B2B"
                        className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                    </div>
                    <div>
                      <Label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>Audiencia</Label>
                      <Input data-testid="vp-audiencia" value={voiceProfile.audiencia}
                        onChange={(e) => setVoiceProfile({ ...voiceProfile, audiencia: e.target.value })}
                        placeholder="Ej: Emprendedores y PYMEs"
                        className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                    </div>
                  </div>
                </div>

                {/* Closing phrase + Affiliate URL */}
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Sparkles className="w-5 h-5" style={{ color: "#A78BFA" }} />
                    Cierre y Producto Propio
                  </h2>
                  <p className="text-sm mb-5" style={{ color: "#94A3B8" }}>
                    Frase con la que cerrar todos tus artículos + (opcional) producto/recurso propio que la IA mencionará cuando encaje naturalmente con el tema.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>Frase de cierre</Label>
                      <Input data-testid="vp-closing-phrase" value={voiceProfile.closing_phrase}
                        onChange={(e) => setVoiceProfile({ ...voiceProfile, closing_phrase: e.target.value })}
                        placeholder="Ej: Un abrazo conSentido — Raúl"
                        maxLength={200}
                        className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      <p className="text-xs mt-1" style={{ color: "#64748B" }}>Se añade automáticamente al final de cada artículo generado.</p>
                    </div>
                    <Separator style={{ background: "#1E3A5F" }} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>Nombre del producto / recurso</Label>
                        <Input data-testid="vp-affiliate-label" value={voiceProfile.affiliate_label}
                          onChange={(e) => setVoiceProfile({ ...voiceProfile, affiliate_label: e.target.value })}
                          placeholder='Ej: Curso "Meditación 21 días"'
                          maxLength={120}
                          className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      </div>
                      <div>
                        <Label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>URL del producto</Label>
                        <Input data-testid="vp-affiliate-url" value={voiceProfile.affiliate_url}
                          onChange={(e) => setVoiceProfile({ ...voiceProfile, affiliate_url: e.target.value })}
                          placeholder="https://amazon.es/dp/..."
                          className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs block mb-1" style={{ color: "#94A3B8" }}>Descripción breve (qué resuelve, para quién)</Label>
                      <Textarea data-testid="vp-affiliate-description" value={voiceProfile.affiliate_description}
                        onChange={(e) => setVoiceProfile({ ...voiceProfile, affiliate_description: e.target.value })}
                        placeholder="Ej: Programa de 21 días para reducir el estrés con meditación guiada, ideal para personas que empiezan."
                        rows={2}
                        maxLength={400}
                        className="text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0", fontFamily: "'Outfit'" }} />
                      <p className="text-xs mt-1" style={{ color: "#64748B" }}>
                        🧠 La IA decidirá si mencionarlo. Solo aparecerá si encaja con el tema del artículo (no fuerza la mención).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Voice Dimensions */}
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Mic className="w-5 h-5" style={{ color: "#00D4FF" }} />
                    Perfil de Voz
                  </h2>
                  <p className="text-sm mb-5" style={{ color: "#94A3B8" }}>
                    Configura las 5 dimensiones de tu voz de marca. Se aplican a todos los articulos generados.
                  </p>
                  <div className="space-y-5">
                    <DimensionSelector label="Formalidad" options={FORMALIDAD_OPTIONS}
                      value={voiceProfile.formalidad} onChange={(v) => setVoiceProfile({ ...voiceProfile, formalidad: v })}
                      testPrefix="vp-formalidad" />
                    <Separator style={{ background: "#1E3A5F" }} />
                    <DimensionSelector label="Cercania" options={CERCANIA_OPTIONS}
                      value={voiceProfile.cercania} onChange={(v) => setVoiceProfile({ ...voiceProfile, cercania: v })}
                      testPrefix="vp-cercania" />
                    <Separator style={{ background: "#1E3A5F" }} />
                    <DimensionSelector label="Energia" options={ENERGIA_OPTIONS}
                      value={voiceProfile.energia} onChange={(v) => setVoiceProfile({ ...voiceProfile, energia: v })}
                      testPrefix="vp-energia" />
                    <Separator style={{ background: "#1E3A5F" }} />
                    <DimensionSelector label="Postura" options={POSTURA_OPTIONS}
                      value={voiceProfile.postura} onChange={(v) => setVoiceProfile({ ...voiceProfile, postura: v })}
                      testPrefix="vp-postura" />
                    <Separator style={{ background: "#1E3A5F" }} />

                    {/* Tonos Expresivos */}
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold block" style={{ color: "#E2E8F0" }}>
                        Tono Expresivo <span className="font-normal" style={{ color: "#94A3B8" }}>(elige hasta 3)</span>
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {TONOS_HABITUALES.map((t) => (
                          <button key={t} data-testid={`vp-tono-${t}`} onClick={() => toggleTono(t)}
                            className="px-4 py-2 rounded-full text-sm border transition-all duration-200"
                            style={{
                              background: voiceProfile.tonos_expresivos.includes(t) ? "rgba(0,212,255,0.1)" : "transparent",
                              borderColor: voiceProfile.tonos_expresivos.includes(t) ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                              color: voiceProfile.tonos_expresivos.includes(t) ? "#00D4FF" : "#94A3B8",
                            }}>
                            {t}
                          </button>
                        ))}
                      </div>
                      {/* Advanced tones */}
                      <button onClick={() => setShowAdvancedTones(!showAdvancedTones)}
                        className="flex items-center gap-1 text-xs mt-1"
                        style={{ color: "#64748B" }}>
                        <ChevronDown className={`w-3 h-3 transition-transform ${showAdvancedTones ? "rotate-180" : ""}`} />
                        Tonos con caracter
                      </button>
                      {showAdvancedTones && (
                        <div className="flex flex-wrap gap-2 mt-1 animate-fade-in-up">
                          {TONOS_CARACTER.map((t) => (
                            <button key={t} data-testid={`vp-tono-${t}`} onClick={() => toggleTono(t)}
                              className="px-4 py-2 rounded-full text-sm border transition-all duration-200"
                              style={{
                                background: voiceProfile.tonos_expresivos.includes(t) ? "rgba(249,115,22,0.1)" : "transparent",
                                borderColor: voiceProfile.tonos_expresivos.includes(t) ? "rgba(249,115,22,0.4)" : "#1E3A5F",
                                color: voiceProfile.tonos_expresivos.includes(t) ? "#F97316" : "#64748B",
                              }}>
                              {t}
                            </button>
                          ))}
                        </div>
                      )}
                      {voiceProfile.tonos_expresivos.length > 0 && (
                        <div className="flex gap-1.5 mt-2">
                          {voiceProfile.tonos_expresivos.map((t) => (
                            <span key={t} className="px-2.5 py-1 rounded-full text-xs flex items-center gap-1"
                              style={{ background: "rgba(0,212,255,0.08)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.2)" }}>
                              {t}
                              <button onClick={() => toggleTono(t)} className="ml-0.5 hover:opacity-70">&times;</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <Separator style={{ background: "#1E3A5F" }} />

                    {/* Referencia de Estilo */}
                    <div>
                      <Label className="text-xs font-semibold block mb-1" style={{ color: "#E2E8F0" }}>Referencia de Estilo</Label>
                      <Input data-testid="vp-referencia" value={voiceProfile.referencia_estilo}
                        onChange={(e) => setVoiceProfile({ ...voiceProfile, referencia_estilo: e.target.value })}
                        placeholder="¿Hay algun autor o comunicador cuyo estilo admiras?"
                        className="h-9 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      <p className="text-xs mt-1" style={{ color: "#475569" }}>Opcional. Ej: Seth Godin, Isra Garcia, Tim Ferriss</p>
                    </div>
                  </div>
                </div>

                {/* Voice Sample Upload */}
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <FileText className="w-4 h-4" style={{ color: "#F97316" }} />
                    Muestra de Voz Propia
                  </h2>
                  <p className="text-xs mb-4" style={{ color: "#94A3B8" }}>
                    Sube un texto que hayas escrito (articulo, post, email, presentacion). La Forja analizara tu estilo y lo usara para que tu contenido suene a ti.
                  </p>
                  <div className="flex items-start gap-4">
                    <input ref={voiceFileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" onChange={handleVoiceUpload} />
                    <Button data-testid="upload-voice-btn" variant="outline" onClick={() => voiceFileRef.current?.click()}
                      disabled={analyzingVoice}
                      className="rounded-full text-xs"
                      style={{ borderColor: "#F97316", color: "#F97316", background: "rgba(249,115,22,0.05)" }}>
                      {analyzingVoice ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                      {analyzingVoice ? "Analizando..." : "Subir muestra"}
                    </Button>
                    <div className="text-xs" style={{ color: "#64748B" }}>
                      PDF, DOCX o TXT. Min 150 palabras, max 4.000 palabras recomendadas. Max 5MB.
                    </div>
                  </div>

                  {/* Analysis Results */}
                  {voiceAnalysis && (
                    <div className="mt-4 rounded-lg border p-4 animate-fade-in-up" style={{ background: "#0B1622", borderColor: "rgba(0,212,255,0.2)" }}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold" style={{ color: "#00D4FF" }}>Analisis completado</h4>
                        <Button data-testid="apply-analysis-btn" size="sm" onClick={applyAnalysis}
                          className="rounded-full text-xs h-7 px-3"
                          style={{ background: "rgba(0,212,255,0.1)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.3)" }}>
                          <Check className="w-3 h-3 mr-1" />Aplicar valores
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { label: "Registro", value: voiceAnalysis.formalidad },
                          { label: "Cercania", value: voiceAnalysis.cercania },
                          { label: "Energia", value: voiceAnalysis.energia },
                          { label: "Postura", value: voiceAnalysis.postura },
                        ].map((item) => (
                          <div key={item.label}>
                            <span className="text-xs block" style={{ color: "#64748B" }}>{item.label}</span>
                            <span className="text-xs font-medium" style={{ color: "#E2E8F0" }}>{item.value}</span>
                          </div>
                        ))}
                      </div>
                      {voiceAnalysis.rasgos_destacados && voiceAnalysis.rasgos_destacados.length > 0 && (
                        <div className="mt-3">
                          <span className="text-xs block mb-1" style={{ color: "#64748B" }}>Rasgos destacados</span>
                          <div className="flex flex-wrap gap-1">
                            {voiceAnalysis.rasgos_destacados.map((r, i) => (
                              <span key={i} className="px-2 py-0.5 rounded text-xs" style={{ background: "rgba(249,115,22,0.08)", color: "#F97316" }}>{r}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {voiceAnalysis.resumen_voz && (
                        <div className="mt-3">
                          <span className="text-xs block mb-1" style={{ color: "#64748B" }}>Resumen de voz</span>
                          <p className="text-xs" style={{ color: "#CBD5E1" }}>{voiceAnalysis.resumen_voz}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current voice summary */}
                  {voiceProfile.resumen_voz && !voiceAnalysis && (
                    <div className="mt-4 rounded-lg border p-3" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                      <span className="text-xs block mb-1" style={{ color: "#64748B" }}>Resumen de voz actual</span>
                      <p className="text-xs" style={{ color: "#CBD5E1" }}>{voiceProfile.resumen_voz}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ═══ ÁNGULO EDITORIAL (Bloque 2) ═══ */}
            {activeSection === "angle" && (
              <div className="space-y-4 animate-fade-in-up">
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Target className="w-5 h-5" style={{ color: "#00D4FF" }} />
                    Angulo Editorial
                  </h2>
                  <p className="text-sm mb-5" style={{ color: "#94A3B8" }}>
                    Linea editorial base para todos tus articulos. Define como se estructura el contenido.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {ANGULO_OPTIONS.map((opt) => (
                      <button key={opt.id} data-testid={`angle-${opt.id}`}
                        onClick={() => setAnguloEditorial(opt.id)}
                        className="rounded-xl p-4 text-left border transition-all duration-200"
                        style={{
                          background: anguloEditorial === opt.id ? "rgba(0,212,255,0.06)" : "#0B1622",
                          borderColor: anguloEditorial === opt.id ? "rgba(0,212,255,0.3)" : "#1E3A5F",
                        }}>
                        <div className="text-sm font-medium" style={{ color: anguloEditorial === opt.id ? "#00D4FF" : "#E2E8F0" }}>
                          {opt.label}
                        </div>
                        {anguloEditorial === opt.id && <Check className="w-3 h-3 mt-1" style={{ color: "#00D4FF" }} />}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Target className="w-5 h-5" style={{ color: "#F97316" }} />
                    Objetivo del Contenido
                  </h2>
                  <p className="text-sm mb-5" style={{ color: "#94A3B8" }}>
                    Define que quieres conseguir con tu contenido. Se aplica como linea base.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {OBJETIVO_OPTIONS.map((opt) => (
                      <button key={opt.id} data-testid={`obj-${opt.id}`}
                        onClick={() => setObjetivoContenido(opt.id)}
                        className="rounded-xl p-4 text-left border transition-all duration-200"
                        style={{
                          background: objetivoContenido === opt.id ? "rgba(249,115,22,0.06)" : "#0B1622",
                          borderColor: objetivoContenido === opt.id ? "rgba(249,115,22,0.3)" : "#1E3A5F",
                        }}>
                        <div className="text-sm font-medium" style={{ color: objetivoContenido === opt.id ? "#F97316" : "#E2E8F0" }}>
                          {opt.label}
                        </div>
                        {objetivoContenido === opt.id && <Check className="w-3 h-3 mt-1" style={{ color: "#F97316" }} />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Current config summary */}
                {(anguloEditorial || objetivoContenido) && (
                  <div className="rounded-xl border p-4" style={{ background: "rgba(0,212,255,0.03)", borderColor: "rgba(0,212,255,0.15)" }}>
                    <h4 className="text-xs font-semibold mb-2" style={{ color: "#00D4FF" }}>Configuracion actual</h4>
                    <div className="flex flex-wrap gap-2">
                      {anguloEditorial && (
                        <span className="px-3 py-1 rounded-full text-xs" style={{ background: "rgba(0,212,255,0.08)", color: "#00D4FF", border: "1px solid rgba(0,212,255,0.2)" }}>
                          Angulo: {anguloEditorial}
                        </span>
                      )}
                      {objetivoContenido && (
                        <span className="px-3 py-1 rounded-full text-xs" style={{ background: "rgba(249,115,22,0.08)", color: "#F97316", border: "1px solid rgba(249,115,22,0.2)" }}>
                          Objetivo: {objetivoContenido}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══ REDES SOCIALES ═══ */}
            {activeSection === "networks" && (
              <div className="space-y-3 animate-fade-in-up">
                <div className="mb-2">
                  <h2 className="text-base font-semibold flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    Redes Sociales
                  </h2>
                  <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>
                    Conecta tus cuentas para publicar con un click desde cada artículo.
                  </p>
                </div>

                {/* Connected accounts via Pipedream Connect — oculto en MVP, roadmap Business */}

                {/* Legacy manual credentials (optional) */}
                <details open>
                  <summary className="text-xs cursor-pointer py-2" style={{ color: "#94A3B8" }}>
                    Redes activas (para filtrar los botones Copiar/Descargar en los artículos)
                  </summary>
                  <div className="mt-3 space-y-3">
                    {NETWORKS.map((net) => (
                      <NetworkCard key={net.id} net={net} data={socialNetworks[net.id]} onChange={(d) => updateNetwork(net.id, d)} />
                    ))}
                  </div>
                </details>
              </div>
            )}

            {/* ═══ ESTILO VISUAL ═══ */}
            {activeSection === "visual" && (
              <div className="space-y-4 animate-fade-in-up">
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-base font-semibold mb-2 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <Palette className="w-4 h-4" style={{ color: "#00D4FF" }} />
                    Motor de IA por Defecto
                  </h2>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {ENGINES.map((e) => (
                      <button key={e.id} data-testid={`pref-engine-${e.id}`}
                        onClick={() => setVisualPrefs({ ...visualPrefs, default_engine: e.id })}
                        className="rounded-xl p-4 text-left border transition-all duration-200"
                        style={{
                          background: visualPrefs.default_engine === e.id ? "rgba(0,212,255,0.06)" : "#0B1622",
                          borderColor: visualPrefs.default_engine === e.id ? "rgba(0,212,255,0.3)" : "#1E3A5F",
                        }}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium" style={{ color: visualPrefs.default_engine === e.id ? "#00D4FF" : "#E2E8F0" }}>{e.label}</span>
                          {visualPrefs.default_engine === e.id && <Check className="w-4 h-4" style={{ color: "#00D4FF" }} />}
                        </div>
                        <span className="text-xs" style={{ color: "#94A3B8" }}>{e.desc}</span>
                      </button>
                    ))}
                  </div>
                  <Separator style={{ background: "#1E3A5F" }} />
                  <div className="mt-5">
                    <Label className="text-xs block mb-2" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>Estilo visual general</Label>
                    <div className="flex flex-wrap gap-2">
                      {STYLES.map((s) => (
                        <button key={s.id} data-testid={`pref-style-${s.id}`}
                          onClick={() => setVisualPrefs({ ...visualPrefs, default_style: s.id })}
                          className="px-4 py-2 rounded-full text-sm border transition-all duration-200"
                          style={{
                            background: visualPrefs.default_style === s.id ? "rgba(0,212,255,0.1)" : "transparent",
                            borderColor: visualPrefs.default_style === s.id ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                            color: visualPrefs.default_style === s.id ? "#00D4FF" : "#94A3B8",
                          }}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Separator className="mt-5" style={{ background: "#1E3A5F" }} />

                  {/* Estilo secundario (combinar estilos) */}
                  <div className="mt-5">
                    <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>
                      Estilo secundario (opcional — se combina con el principal)
                    </Label>
                    <select
                      data-testid="pref-secondary-style"
                      value={visualPrefs.secondary_style || ""}
                      onChange={(e) => setVisualPrefs({ ...visualPrefs, secondary_style: e.target.value || undefined })}
                      className="w-full h-10 px-3 rounded-lg text-sm border"
                      style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }}
                    >
                      <option value="">— Ninguno —</option>
                      {STYLES.filter((s) => s.id !== visualPrefs.default_style).map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Mood / Ambiente */}
                  <div className="mt-4">
                    <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8", fontFamily: "'IBM Plex Mono'" }}>
                      Ambiente / Iluminación (Mood)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        data-testid="pref-mood-none"
                        onClick={() => setVisualPrefs({ ...visualPrefs, mood: undefined })}
                        className="px-4 py-2 rounded-full text-xs border transition-all"
                        style={{
                          background: !visualPrefs.mood ? "rgba(0,212,255,0.1)" : "transparent",
                          borderColor: !visualPrefs.mood ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                          color: !visualPrefs.mood ? "#00D4FF" : "#94A3B8",
                        }}
                      >
                        Por defecto
                      </button>
                      {MOODS.map((m) => (
                        <button
                          key={m.id}
                          data-testid={`pref-mood-${m.id}`}
                          onClick={() => setVisualPrefs({ ...visualPrefs, mood: m.id })}
                          className="px-4 py-2 rounded-full text-xs border transition-all"
                          style={{
                            background: visualPrefs.mood === m.id ? "rgba(0,212,255,0.1)" : "transparent",
                            borderColor: visualPrefs.mood === m.id ? "rgba(0,212,255,0.4)" : "#1E3A5F",
                            color: visualPrefs.mood === m.id ? "#00D4FF" : "#94A3B8",
                          }}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Prompt custom per-artículo removido — ahora se introduce en el momento de generar */}
                  <div className="mt-4 px-3 py-2 rounded-lg text-xs" style={{ background: "rgba(0,212,255,0.04)", border: "1px dashed rgba(0,212,255,0.2)", color: "#94A3B8" }}>
                    💡 La <strong style={{ color: "#00D4FF" }}>descripción adicional</strong> (prompt libre de hasta 200 caracteres) se añadirá
                    al generar cada artículo, así puedes ajustar la imagen según el tema. Los valores de esta pantalla son tus
                    <strong style={{ color: "#00D4FF" }}> valores por defecto</strong>, que se pre-rellenan en cada artículo (y puedes sobrescribir allí).
                  </div>
                </div>
              </div>
            )}

            {/* ═══ MARCA ═══ */}
            {activeSection === "brand" && (
              <div className="space-y-4 animate-fade-in-up">
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <h2 className="text-base font-semibold mb-2 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    <ImageIcon className="w-4 h-4" style={{ color: "#00D4FF" }} />
                    Identidad de Marca
                  </h2>
                  <div className="space-y-5">
                    <div>
                      <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Nombre de empresa</Label>
                      <Input data-testid="brand-company-name" value={brand.company_name}
                        onChange={(e) => setBrand({ ...brand, company_name: e.target.value })}
                        placeholder="Ej: Alianza Empresarial"
                        className="h-10 text-sm" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Color primario</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" data-testid="brand-primary-color" value={brand.primary_color}
                            onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })}
                            className="w-10 h-10 rounded-lg border cursor-pointer" style={{ borderColor: "#1E3A5F", background: "#0B1622" }} />
                          <Input value={brand.primary_color}
                            onChange={(e) => setBrand({ ...brand, primary_color: e.target.value })}
                            className="h-10 text-sm flex-1 font-mono"
                            style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Color secundario</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" data-testid="brand-secondary-color" value={brand.secondary_color}
                            onChange={(e) => setBrand({ ...brand, secondary_color: e.target.value })}
                            className="w-10 h-10 rounded-lg border cursor-pointer" style={{ borderColor: "#1E3A5F", background: "#0B1622" }} />
                          <Input value={brand.secondary_color}
                            onChange={(e) => setBrand({ ...brand, secondary_color: e.target.value })}
                            className="h-10 text-sm flex-1 font-mono"
                            style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1 h-12 rounded-lg" style={{ background: brand.primary_color }} />
                      <div className="flex-1 h-12 rounded-lg" style={{ background: brand.secondary_color }} />
                    </div>
                    <Separator style={{ background: "#1E3A5F" }} />
                    <div>
                      <Label className="text-xs block mb-1.5" style={{ color: "#94A3B8" }}>Logo de empresa</Label>
                      <div className="flex items-start gap-4">
                        {brand.logo_base64 ? (
                          <div className="w-20 h-20 rounded-xl border overflow-hidden flex items-center justify-center"
                            style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                            <img src={brand.logo_base64} alt="Logo" className="max-w-full max-h-full object-contain" />
                          </div>
                        ) : (
                          <div className="w-20 h-20 rounded-xl border flex items-center justify-center"
                            style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                            <Upload className="w-6 h-6" style={{ color: "#475569" }} />
                          </div>
                        )}
                        <div className="flex-1">
                          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} />
                          <Button data-testid="upload-logo-btn" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                            className="rounded-full text-xs" style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}>
                            <Upload className="w-3.5 h-3.5 mr-1.5" />
                            {brand.logo_base64 ? "Cambiar logo" : "Subir logo"}
                          </Button>
                          <p className="text-xs mt-1.5" style={{ color: "#64748B" }}>PNG, JPG, WebP o SVG. Max 2MB.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ WORDPRESS ═══ */}
            {activeSection === "wordpress" && (
              <div data-testid="wp-settings-section" className="space-y-4 animate-fade-in-up">
                <div className="rounded-xl border p-6" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
                  <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
                    <div>
                      <h2 className="text-lg font-semibold mb-1 flex items-center gap-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                        <FileText className="w-5 h-5" style={{ color: "#21759B" }} />
                        WordPress
                      </h2>
                      <p className="text-sm" style={{ color: "#94A3B8" }}>
                        Conecta tu sitio WordPress para publicar artículos directamente como borrador, programados o publicados.
                      </p>
                    </div>
                    {wpConfig.configured && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs"
                        style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }}>
                        <Check className="w-3 h-3" /> Configurado
                      </span>
                    )}
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs mb-1.5 block" style={{ color: "#94A3B8" }}>URL del sitio</Label>
                      <Input data-testid="wp-url-input" value={wpConfig.url}
                        onChange={(e) => { setWpConfig({ ...wpConfig, url: e.target.value }); setWpDirty(true); }}
                        placeholder="https://tudominio.com"
                        className="h-10" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      <p className="text-xs mt-1" style={{ color: "#64748B" }}>Sin "/wp-admin", solo el dominio raíz.</p>
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block" style={{ color: "#94A3B8" }}>Usuario WordPress</Label>
                      <Input data-testid="wp-username-input" value={wpConfig.username}
                        onChange={(e) => { setWpConfig({ ...wpConfig, username: e.target.value }); setWpDirty(true); }}
                        placeholder="admin"
                        className="h-10" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                    </div>
                    <div>
                      <Label className="text-xs mb-1.5 block" style={{ color: "#94A3B8" }}>
                        Application Password
                        {wpConfig.has_password && <span className="ml-2 text-xs" style={{ color: "#4ADE80" }}>(guardada • déjala vacía para mantenerla)</span>}
                      </Label>
                      <Input data-testid="wp-app-password-input" type="password" value={wpConfig.app_password}
                        onChange={(e) => { setWpConfig({ ...wpConfig, app_password: e.target.value }); setWpDirty(true); }}
                        placeholder={wpConfig.has_password ? "•••• •••• •••• ••••" : "xxxx xxxx xxxx xxxx"}
                        className="h-10" style={{ background: "#0B1622", borderColor: "#1E3A5F", color: "#E2E8F0" }} />
                      <p className="text-xs mt-1" style={{ color: "#64748B" }}>
                        Genérala desde tu WordPress: <strong>Usuarios &gt; Tu perfil &gt; Application Passwords</strong>. NO uses tu contraseña de login.
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button data-testid="wp-save-btn" onClick={handleSaveWordPress} disabled={saving || !wpDirty}
                        className="rounded-full px-5 text-sm font-medium"
                        style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Guardar WordPress
                      </Button>
                      <Button data-testid="wp-test-btn" onClick={handleTestWordPress} disabled={wpTestState.loading || !wpConfig.configured}
                        variant="outline" className="rounded-full px-5 text-sm font-medium"
                        style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}>
                        {wpTestState.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                        Probar conexión
                      </Button>
                      {wpConfig.configured && (
                        <Button data-testid="wp-delete-btn" onClick={handleDeleteWordPress} variant="ghost"
                          className="rounded-full px-4 text-sm font-medium"
                          style={{ color: "#ef4444" }}>
                          Eliminar
                        </Button>
                      )}
                    </div>

                    {wpTestState.ok !== null && (
                      <div data-testid="wp-test-result" className="rounded-lg p-3 text-sm flex items-start gap-2"
                        style={{
                          background: wpTestState.ok ? "rgba(74,222,128,0.08)" : "rgba(239,68,68,0.08)",
                          border: `1px solid ${wpTestState.ok ? "rgba(74,222,128,0.3)" : "rgba(239,68,68,0.3)"}`,
                          color: wpTestState.ok ? "#4ADE80" : "#ef4444",
                        }}>
                        {wpTestState.ok ? <Check className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                        <span>{wpTestState.msg}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border p-5" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
                  <h3 className="text-sm font-semibold mb-2" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>
                    Cómo generar una Application Password
                  </h3>
                  <ol className="list-decimal pl-5 space-y-1 text-xs" style={{ color: "#94A3B8", lineHeight: 1.7 }}>
                    <li>Inicia sesión en tu WordPress como administrador.</li>
                    <li>Ve a <strong style={{ color: "#E2E8F0" }}>Usuarios → Perfil</strong>.</li>
                    <li>Baja hasta <strong style={{ color: "#E2E8F0" }}>Application Passwords</strong> (Contraseñas de aplicación).</li>
                    <li>Escribe un nombre (p. ej. "La Forja") y pulsa <strong style={{ color: "#E2E8F0" }}>Add New Application Password</strong>.</li>
                    <li>Copia la contraseña con espacios y pégala arriba. Solo se muestra una vez.</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const PIPEDREAM_NETWORKS = [
  { id: "linkedin", label: "LinkedIn", color: "#0A66C2", icon: Linkedin, note: "Publica en tu perfil u organización" },
  { id: "x", label: "X (Twitter)", color: "#1DA1F2", icon: null, note: "Publica tweets desde tu cuenta" },
  { id: "facebook", label: "Facebook Pages", color: "#1877F2", icon: Facebook, note: "Requiere aprobación de Meta" },
  { id: "instagram", label: "Instagram Business", color: "#E1306C", icon: Instagram, note: "Requiere aprobación de Meta + cuenta Business" },
];

function PipedreamNetworks() {
  const { user } = useAuth();
  const { connect, disconnect, connections, loading, busyNetwork, error } = usePipedreamConnect(user?.id);

  const getConnForNetwork = (id) => connections.find((c) => c.network === id && c.is_active);

  return (
    <div className="space-y-2">
      {error && (
        <div className="px-3 py-2 rounded-md text-xs" style={{ background: "rgba(239,68,68,0.08)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
          {error}
        </div>
      )}
      {PIPEDREAM_NETWORKS.map((net) => {
        const conn = getConnForNetwork(net.id);
        const isConnected = !!conn;
        const isBusy = busyNetwork === net.id;
        const Icon = net.icon;
        return (
          <div
            key={net.id}
            data-testid={`pd-network-${net.id}`}
            className="flex items-center gap-3 p-3 rounded-xl border"
            style={{
              background: isConnected ? "rgba(74,222,128,0.04)" : "#0B1622",
              borderColor: isConnected ? "rgba(74,222,128,0.2)" : "#1E3A5F",
            }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${net.color}15`, color: net.color }}
            >
              {Icon ? <Icon className="w-4 h-4" /> : <span className="text-xs font-bold">X</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: "#E2E8F0" }}>{net.label}</span>
                {isConnected && (
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(74,222,128,0.1)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.2)" }}>
                    Conectado
                  </span>
                )}
              </div>
              <div className="text-xs mt-0.5 truncate" style={{ color: "#64748B" }}>
                {isConnected ? (conn.account_name || "Cuenta autorizada") : net.note}
              </div>
            </div>
            {isConnected ? (
              <Button
                data-testid={`pd-disconnect-${net.id}`}
                size="sm"
                variant="outline"
                disabled={loading}
                onClick={() => disconnect(conn.id)}
                className="text-xs h-8"
                style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}
              >
                Desconectar
              </Button>
            ) : (
              <Button
                data-testid={`pd-connect-${net.id}`}
                size="sm"
                disabled={loading}
                onClick={() => connect(net.id)}
                className="text-xs h-8"
                style={{ background: `${net.color}`, color: "white" }}
              >
                {isBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Link2 className="w-3.5 h-3.5 mr-1" />}
                {isBusy ? "Conectando..." : "Conectar"}
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

